/**
 * Multi-criteria filter panel for narrowing simulation scores by group, employee, product, and SIM.
 *
 * @module components/dashboard/ScoreFilters
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import DehazeIcon from '@material-ui/icons/Dehaze';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import Checkbox from "@material-ui/core/Checkbox";
import AssignmentIcon from '@material-ui/icons/Assignment';
import {Paper, Tooltip} from '@material-ui/core';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Collapse from '@material-ui/core/Collapse';
import ScoreFilterStyle from './ScoreFilters.js';
import { getSimsData } from '../../services/result.service';
import {
    fetchSelectGroups,
    fetchEmployees,
} from '../../redux/admin/action';
import {
    fetchProductList,
} from '../../redux/sims/actions';
import Select from "@material-ui/core/Select";

/**
 * Represents a component that allows users to filter data based on scores using various parameters
 * such as groups, employees, products, and simulations. This component provides state management
 * and event handlers to enable real-time updates and user interactions with the filter interface.
 *
 * @param {Object} props - The properties passed to the `ScoreFilters` component.
 * @param {Function} props.fetchSelectGroups - A function to fetch the list of selectable groups.
 * @param {Array} props.selectGroups - An array containing the list of available groups for selection.
 * @param {Function} props.fetchEmployees - A function to fetch the list of employees.
 * @param {Array} props.employees - An array containing employee data.
 * @param {Function} props.fetchProductList - A function to retrieve a list of available products.
 * @param {Array} props.products - An array containing the list of products.
 * @param {Function} props.productChange - A callback function triggered when product-related changes occur.
 * @param {string} props.defaultProduct - The default product used for initializing the application state.
 * @param {Function} props.setDefaultProduct - A function to update the default product.
 */
const ScoreFilters = ({
    fetchSelectGroups,
    selectGroups,
    fetchEmployees,
    employees,
    fetchProductList,
    products,
    productChange,
    defaultProduct,
    setDefaultProduct
}) => {
    const useStyles = makeStyles((theme) => ScoreFilterStyle(theme));
    const classes = useStyles();

    const [openGroup, setOpenGroup] = React.useState(false);
    const [openEmployee, setOpenEmployee] = useState(false);
    const [openSims, setOpenSims] = useState(false);
    const [unAssigned, setunAssigned] = useState(false);
    const [assigned, setAssigned] = useState(false);
    const [sims, setSims] = useState([]);
    const [state, setState] = useState({
        product: '',
        groups: [],
        members: [],
        sims: [],
        assigned: true,
        un_assigned: false,
    });
    const stringComparator = Intl.Collator(
        undefined,
        {
            numeric: true,
            variant: 'case', ifnorePunctuation: 'true'
        }
    );

    useEffect(() => {
        fetchSelectGroups();
        fetchEmployees();
        fetchProductList();
        //setProducts(companyDetails.products);
    }, [fetchSelectGroups, fetchEmployees, fetchProductList]);

    useEffect(() => {
        if (products.length) {
            setState({
                ...state,
                product: defaultProduct,
            });

            productChange(state);
        }
    }, [products]);

    useEffect(
        () => {
            if (defaultProduct?.length) {
                getSimsData(defaultProduct)
                    .then((res) => {
                        res.data.map((sim) => {
                            sim.open = false;
                            return sim;
                        });
                        setSims(res.data.sort((sim1, sim2) => { return stringComparator.compare(sim1.title, sim2.title); }));
                    });
            }
        },
        [defaultProduct]
    );

    useEffect(
        () => {
            const newState = {...state, assigned: assigned, un_assigned: unAssigned};

            setState(newState);
            productChange(newState);
        },
        [assigned, unAssigned]
    );

    /**
     * Toggles the current state of the openGroup variable.
     *
     * This function inverses the boolean value of the openGroup state,
     * effectively switching between open and closed states for a group component or element.
     */
    const handleGroupClick = () => {
        setOpenGroup(!openGroup);
    };

    /**
     * Toggles the visibility state of the employee-related UI component.
     *
     * This function updates the state of `openEmployee` to its opposite value,
     * effectively handling click events related to employees.
     */
    const handleEmpClick = () => {
        setOpenEmployee(!openEmployee);
    };

    /**
     * Toggles the visibility state of the simulations panel.
     *
     * This function manages the state of the `openSims` variable by
     * inverting its current boolean value. It is typically used as an
     * onClick handler for elements that control the opening or closing
     * of a simulations-related UI component.
     *
     * No parameters or return values are associated with this function.
     */
    const handleSimClick = () => {
        setOpenSims(!openSims);
    };

    /**
     * Handles the change event for updating product-related state and triggering associated actions.
     *
     * This function is typically used as an event handler for input changes. It updates the application state
     * based on the new product value and performs the following actions:
     * - Updates the corresponding property in the state based on the input's name.
     * - Calls an asynchronous function to fetch data related to the new product and updates associated state.
     * - Triggers a callback function to handle additional logic after the product change.
     * - Updates the default product used within the application.
     *
     * @async
     * @param {Object} e - The event object from the input change.
     * @param {Object} e.target - The target element of the event.
     * @param {string} e.target.value - The new value of the input field.
     * @param {string} e.target.name - The name attribute of the input field.
     * @throws Will propagate any errors thrown by the asynchronous data fetching function.
     */
    const handleChangeProduct = async (e) => {
        const newProduct = e.target.value,
            prop = e.target.name;

        setState({
            ...state,
            [prop]: newProduct,
        });

        state.product = newProduct;
        await getSimsData(newProduct)
            .then((res) => {
                setSims(res.data);
            });
        productChange(state);

        setDefaultProduct(newProduct);
    };

    /**
     * Handles checkbox click events for updating the `state` object based on user interactions.
     *
     * This function processes checkbox changes for two categories: `members` and `groups`.
     * If a checkbox is clicked, the corresponding value is either added to or removed from
     * the `state.members` or `state.groups` array depending on its current presence in the array.
     *
     * @param {Event} e - The event object associated with the checkbox change.
     * @property {string} e.target.name - The name attribute of the checkbox being clicked.
     * @property {string} e.target.value - The value attribute of the checkbox being clicked.
     *
     * Modifies:
     * - `state.members` if the checkbox name begins with 'members'.
     * - `state.groups` if the checkbox name begins with 'groups'.
     *
     * Triggers:
     * - Calls the `productChange` function with the updated `state` after changes are applied.
     */
    const handleCheckboxClicks = (e) => {
        if (e.target.name.indexOf('members') === 0) {
            const index = state.members.indexOf(e.target.value);
            if (index > -1) {
                state.members.splice(index, 1);
            } else {
                state.members.push(e.target.value);
            }
        } else if (e.target.name.indexOf('groups') === 0) {
            const index = state.groups.indexOf(e.target.value);
            if (index > -1) {
                state.groups.splice(index, 1);
            } else {
                state.groups.push(e.target.value);
            }
        }
        productChange(state);
    };

    /**
     * Toggles the open state of the given simulation in the `sims` array and
     * updates the `state.sims` array by either adding or removing the simulation
     * based on its current presence. Then triggers product change logic and updates
     * the `sims` state.
     *
     * @param {Object} sim - The simulation object to be processed.
     * @param {string} sim._id - The unique identifier for the simulation.
     */
    const handleCheckboxSimClicks = (sim) => {
        sims.filter((simm/* , i */) => {
            if (simm._id === sim._id) {
                simm.open = !(simm.open);
            }
            return simm;
        });
        let index = -1;
        state.sims.filter((simz, i) => {
            if (simz.sim_id === sim._id) {
                index = i;
            }
            return simz;
        });
        if (index > -1) {
            state.sims.splice(index, 1);
        } else {
            state.sims.push({ sim_id: sim._id });
        }
        productChange(state);
        setSims(sims);
    };

    /**
     * Handles the click event for a checkbox associated with a specific level.
     *
     * This function updates the `levels` property of the corresponding simulation object in the `state.sims` array based on the provided level.
     * If the level's `_id` is already in the `levels` array, it removes the `_id`.
     * If it is not present, it adds the `_id` to the array.
     * When the `levels` property is undefined, it initializes it as an empty array before performing the operation.
     * Once the state is updated, the `productChange` function is invoked to propagate the changes.
     *
     * @param {Object} level - The level object associated with the checkbox that was clicked.
     *                         It should include an `_id` property to identify the level and a `sim_id` property to match with a simulation.
     */
    const handleCheckboxLevelClicks = (level) => {
        state.sims.map((sim, i) => {
            if (sim.sim_id === level.sim_id) {
                state.sims[i].levels = state.sims[i].levels === undefined ? [] : state.sims[i].levels;
                if (state.sims[i].levels.length) {
                    const index = state.sims[i].levels.indexOf(level._id);
                    if (index > -1) {
                        state.sims[i].levels.splice(index, 1);
                    } else {
                        state.sims[i].levels.push(level._id);
                    }
                } else {
                    state.sims[i].levels.push(level._id);
                }
            }
            return sim;
        });
        productChange(state);
    };

    /**
     * Handles the click events for assignment-related checkboxes and updates the state accordingly.
     *
     * @param {Object} e - The event object triggered by a checkbox click.
     * @param {Object} e.target - The target element that triggered the event.
     * @param {string} e.target.name - The name attribute of the clicked checkbox, used to determine which state to update.
     * @param {boolean} e.target.checked - The checked status of the clicked checkbox.
     */
    const handleAssignmentCheckboxClicks = (e) => {
        if (e.target.name === 'assigned') {
            setAssigned(e.target.checked);
            setunAssigned(!e.target.checked);
        } else {
            setunAssigned(e.target.checked);
            setAssigned(!e.target.checked);
        }
    };

    /**
     * Handles the click event on a checkbox label by programmatically triggering a click event
     * on the associated checkbox element.
     *
     * @param {string} elem - The name attribute of the checkbox element to be triggered.
     */
    const handleCBLabelClick = (elem) => {
        const node = document.querySelector('[type="checkbox"][name="'+elem+'"]');

        if (node) {
            const event = new MouseEvent ('click', { bubbles: true });
            node.dispatchEvent (event);
        }
    };

    /**
     * Renders a product selection dropdown.
     *
     * This function returns a dropdown component that allows users to select a product
     * from a sorted list. The component uses a select input with an outlined style
     * and supports native dropdown behavior.
     *
     * The list of products provided is sorted alphabetically based on the product names
     * before being displayed in the dropdown. Each product is represented by an option
     * element, identified by its unique ID (`_id`), and the selected value is managed by
     * the `defaultProduct` state.
     *
     * The `onChange` event handler is triggered when the user selects a different product,
     * invoking the `handleChangeProduct` function to update the selected value.
     *
     * @function showProductSelector
     * @returns {JSX.Element} A dropdown component for selecting a product.
     */
    const showProductSelector = () => {
        return (
            <Select
                fullWidth
                variant="outlined"
                className={classes.select}
                value={defaultProduct}
                name="product"
                onChange={handleChangeProduct}
                native
            >
                {products.length && products.sort(
                    (prod1, prod2) => {
                        return stringComparator.compare(prod1.name, prod2.name);
                    }
                ).map(
                    (product) => (
                        <option className={classes.option}
                                key={product._id}
                                value={product._id}
                        >
                            {product.name}
                        </option>
                    ),
                )}
            </Select>
        );
    }

    if (!assigned && !unAssigned) {
        setAssigned(true);
        setunAssigned(false);
        setState({...state, assigned: true, un_assigned: false});
    }

    return (
        <Paper elevation={0} className={classes.scroll}>
            <List component="nav" aria-label="main mailbox folders">
                <ListItem>
                    <ListItemIcon className={classes.toolIcon}>
                        <DehazeIcon />
                    </ListItemIcon>
                    <ListItemText className={classes.textool}><b>Tool</b></ListItemText>
                </ListItem>
                <ListItem>
                    {showProductSelector()}
                </ListItem>
                <ListItem>
                    <ListItemIcon className={classes.toolIconAssignment}>
                        <AssignmentIcon />
                    </ListItemIcon>
                    <ListItemText className={classes.texts}><b>Assignments</b></ListItemText>
                </ListItem>
                <ListItem dense button className={classes.listItem}>
                    <ListItemIcon className={classes.toolIcon}>
                        <Checkbox
                            edge="start"
                            name="assigned"
                            checked={assigned}
                            className={classes.chkbox}
                            onChange={handleAssignmentCheckboxClicks}
                        />
                    </ListItemIcon>
                    <ListItemText
                        className={classes.assignmentTxt}
                        name="assigned"
                        onClick={() => { handleCBLabelClick("assigned");} }
                    >
                        Assigned Only
                    </ListItemText>
                </ListItem>
                <ListItem dense button className={classes.listItem}>
                    <ListItemIcon className={classes.toolIcon}>
                        <Checkbox
                            edge="start"
                            name="unassigned"
                            className={classes.chkbox}
                            checked={unAssigned}
                            onChange={handleAssignmentCheckboxClicks}
                        />
                    </ListItemIcon>
                    <ListItemText className={classes.assignmentTxt}
                        onClick={() => { handleCBLabelClick("unassigned");} }
                    >
                        Unassigned Only
                    </ListItemText>
                </ListItem>
                <ListItem>
                    <ListItemIcon className={classes.toolIcon}>
                        <GroupIcon />
                    </ListItemIcon>
                    <ListItemText className={classes.texts}><b>Groups</b></ListItemText>
                </ListItem>
                {
                    selectGroups.length ? selectGroups.sort(
                        (grp1, grp2) => {
                            return stringComparator.compare(grp1.name, grp2.name);
                        }
                    ).map((group, index) => {
                        if (index < 6 || openGroup) {
                            return (
                                <ListItem dense button key={group._id} className={classes.listItem}>
                                    <ListItemIcon className={classes.toolIcon}>
                                        <Checkbox
                                            edge="start"
                                            name={"groups"+group._id}
                                            value={group._id}
                                            className={classes.chkbox}
                                            onChange={handleCheckboxClicks}
                                        />
                                    </ListItemIcon>
                                    <ListItemText onClick={() => { handleCBLabelClick("groups"+group._id);} }>
                                        {group.name}
                                    </ListItemText>
                                </ListItem>
                            );
                        } else {
                            return null;
                        }
                    }) : ''
                }
                {selectGroups.length > 6
                    ? openGroup
                        ? (
                            <ListItemText onClick={handleGroupClick}>
                                <span className={classes.showHide}>{`Hide all groups (${selectGroups.length})`}</span>
                            </ListItemText>
                        )
                        : (
                            <ListItemText onClick={handleGroupClick}>
                <span className={classes.showHide}>
                  {`Show all groups (${selectGroups.length})`}
                </span>
                            </ListItemText>
                        )
                    : ''}
            </List>
            <ListItem>
                <ListItemIcon className={classes.toolIcon}>
                    <PersonIcon />
                </ListItemIcon>
                <ListItemText className={classes.texts}><b>Members</b></ListItemText>
            </ListItem>
            {
                employees.length ? employees.sort(
                    (emp1, emp2) => {
                        let name1 = `${emp1.first_name} ${emp2.first_name}`,
                            name2 = `${emp2.first_name} ${emp2.last_name}`;
                        return stringComparator.compare(name1, name2);
                    }
                ).map((emp, index) => {
                    if (index < 6 || openEmployee) {
                        return (
                            <ListItem dense button key={emp._id} className={classes.listItem}>
                                <ListItemIcon className={classes.toolIcon}>
                                    <Checkbox
                                        name={"members"+emp._id}
                                        edge="start"
                                        value={emp._id}
                                        className={classes.chkbox}
                                        onChange={handleCheckboxClicks}
                                    />
                                </ListItemIcon>
                                <ListItemText onClick={() => { handleCBLabelClick("members"+emp._id);} }>
                                    {`${emp.first_name} ${emp.last_name}`}
                                </ListItemText>
                            </ListItem>
                        );
                    } else {
                        return null;
                    }
                }) : ''
            }
            {employees.length > 6
                ? openEmployee
                    ? (
                        <ListItemText onClick={handleEmpClick}>
                            <span className={classes.showHide}>{`Hide all members (${employees.length})`}</span>
                        </ListItemText>
                    )
                    : (
                        <ListItemText onClick={handleEmpClick}>
                            <span className={classes.showHide}>{`Show all members (${employees.length})`}</span>
                        </ListItemText>
                    )
                : '' }
            <ListItem>
                <ListItemIcon className={classes.toolIcon}>
                    <DehazeIcon />
                </ListItemIcon>
                <ListItemText className={classes.textool}><b>SmartySim</b></ListItemText>
            </ListItem>
            {
                sims.length ? sims.sort(
                    (sim1, sim2) => {
                        return stringComparator.compare(sim1.title, sim2.title);
                    }
                ).map((sim, index) => {
                    if (index < 6 || openSims) {
                        return (
                            <div key={sim._id}>
                                <ListItem dense button className={classes.listItem}>
                                    <ListItemIcon className={classes.toolIcon}>
                                        <Checkbox
                                            edge="start"
                                            value={sim._id}
                                            name={"sim"+sim._id}
                                            onChange={() => handleCheckboxSimClicks(sim)}
                                            className={classes.chkbox}
                                        />
                                    </ListItemIcon>
                                    <Tooltip title={ sim.title === 0 ? 'None' : sim.title}>
                                        <ListItemText onClick={() => { handleCBLabelClick("sim"+sim._id);} }>
                                            {sim?.title?.length > 22
                                                ? `${sim.title.slice(0, 22)}  ...`
                                                : sim.title}
                                        </ListItemText>
                                    </Tooltip>
                                </ListItem>
                                {
                                    sim.simLevel.length > 1 ? (
                                        <Collapse in={sim.open} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {
                                                    sim.simLevel.sort(
                                                        (lvl1, lvl2) => {
                                                            return lvl1.order - lvl2.order;
                                                        }
                                                    ).map((lvl) => (
                                                        <ListItem key={lvl._id} button className={classes.nested}>
                                                            <ListItemIcon className={classes.toolIcon}>
                                                                <Checkbox
                                                                    edge="start"
                                                                    value={lvl._id}
                                                                    name={"simlevel"+lvl._id}
                                                                    onChange={() => handleCheckboxLevelClicks(lvl)}
                                                                    className={classes.chkbox}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText className={classes.font}
                                                                onClick={() => { handleCBLabelClick("simlevel"+lvl._id);} }
                                                            >
                                                                {lvl.name}
                                                            </ListItemText>
                                                        </ListItem>
                                                    ))
                                                }
                                            </List>
                                        </Collapse>
                                    ) : ''
                                }

                            </div>
                        );
                    } else {
                        return null;
                    }
                }) : ''
            }
            {sims.length > 6
                ? openSims
                    ? (
                        <ListItemText onClick={handleSimClick}>
                            <span className={classes.showHide}>
                                {`Hide all sims (${sims.length})`}
                            </span>
                        </ListItemText>
                    )
                    : (
                        <ListItemText onClick={handleSimClick}>
                            <span className={classes.showHide}>{`Show all sims (${sims.length})`}</span>
                        </ListItemText>
                    )
                : ''
            }
        </Paper>

    );
};

ScoreFilters.propTypes = {
    selectGroups: PropTypes.array,
    fetchSelectGroups: PropTypes.func,
    fetchEmployees: PropTypes.func,
    fetchProductList: PropTypes.func,
    employees: PropTypes.array,
    products: PropTypes.array,
    productChange: PropTypes.func,
    defaultProduct: PropTypes.string,
    setDefaultProduct: PropTypes.func,
};

const mapStateToProps = (state) => ({
    selectGroups: state.admin.selectGroups,
    employees: state.admin.employees ? state.admin.employees : [],
    products: state.sim.products ? state.sim.products : [],
});

const mapDispatchToProps = {
    fetchSelectGroups,
    fetchEmployees,
    fetchProductList,
};

export default connect(mapStateToProps, mapDispatchToProps)(ScoreFilters);
